import React, { useContext, useEffect } from 'react';
import { Typography } from 'antd';
import NavigationHeader from '../../Header/NavigationHeader';
import styles from './index.module.css';
import { useNavigate } from 'react-router-dom';
import { pushEventToParent } from '../../../../Utils/eventUtils';
import { formatToINR, formatToINRForStocks } from '../../../../Utils/utils';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { ReactComponent as ProsIcon } from '../../../../Assets/Pros.svg';
import { ReactComponent as CronsIcon } from '../../../../Assets/cons.svg';
import { ReactComponent as PerformanceUpIcon } from '../../../../Assets/PerformanceUp.svg';
import { ReactComponent as PerformanceDownIcon } from '../../../../Assets/PerformanceDown.svg';
import { AppContext } from '../../../../App';
import { getImagePath } from '../../../../Utils/assetUtils';

dayjs.extend(advancedFormat);

interface Props {
  holdingData?: any;
}

const PercentageDisplay = ({ value }: { value: number | null | undefined }) => {
  if (value == null) {
    return <Typography.Text>-</Typography.Text>;
  }

  const isNegative = value < 0;
  const displayValue = `${Math.abs(value).toFixed(2)}%`;

  return (
    <>
      {isNegative ? (
        <>
          <PerformanceDownIcon className={styles.downArrow} />
          <Typography.Text className={styles.negativeValue}>
            {displayValue}
          </Typography.Text>
        </>
      ) : (
        <>
          <PerformanceUpIcon className={styles.upArrow} />
          <Typography.Text className={styles.positiveValue}>
            {displayValue}
          </Typography.Text>
        </>
      )}
    </>
  );
};

const HoldingsScreen: React.FC<Props> = ({ holdingData }) => {
  const navigate = useNavigate();

  const { setBackButtonHandler } = useContext<any>(AppContext);
  useEffect(() => {
    window.onpopstate = () => {
      window.history.pushState(null, '', window.location.href);
    };

    setBackButtonHandler(() => {
      return () => {
        handleBack();
      };
    });
  }, []);

  const formatMarketCap = (marketCap: string) => {
    if (!marketCap) return 'Mid Cap';
    return marketCap
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const currentValue = holdingData
    ? formatToINRForStocks(
        (
          (Number.isNaN(parseInt(holdingData.bsePrices))
            ? 0
            : parseInt(holdingData.bsePrices)) * holdingData.units
        ).toString()
      )
    : '0';

  const quantity = holdingData?.units;
  const companyName = holdingData?.companyName;
  const sector = holdingData?.sector;
  const marketCap = holdingData?.marketCap;

  const handleBack = () => {
    navigate('/stocks', {
      state: {
        fromHoldings: true,
      },
    });
  };

  return (
    <div className={styles.container}>
      <NavigationHeader header='Fund Details' onBack={handleBack} />

      <div className={styles.companyHeader}>
        <div className={styles.companyInfo}>
          <div className={styles.logoContainer}>
            <img
              src={
                holdingData.logo != null
                  ? `https://logo.ignosis.ai/equity/${holdingData.logo}`
                  : getImagePath('images/bank.svg')
              }
              alt={companyName}
              className={styles.companyLogo}
              onError={(e: any) => {
                e.target.src = getImagePath('images/bank.svg');
              }}
            />
          </div>
          <div className={styles.companyDetails}>
            <Typography.Text className={styles.companyName}>
              {companyName}
            </Typography.Text>
            <div className={styles.tagContainer}>
              <div className={styles.midCapTag}>
                <Typography.Text className={styles.tagText}>
                  {formatMarketCap(marketCap)}
                </Typography.Text>
              </div>
              <div className={styles.financeTag}>
                <Typography.Text className={styles.financeTagText}>
                  {sector != null ? sector : 'Other'}
                </Typography.Text>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        {/* Holdings Details Card */}
        <div className={styles.detailsCard}>
          <div className={styles.cardHeader}>
            <Typography.Text className={styles.cardTitle}>
              Overview (as on {dayjs(holdingData.date).format('Do MMM ’YY')})
            </Typography.Text>
          </div>

          <div className={styles.cardContent}>
            <div className={styles.detailsGrid}>
              <div className={styles.detailRow}>
                <div className={styles.detailItem}>
                  <Typography.Text className={styles.detailLabel}>
                    Current Value
                  </Typography.Text>
                  <Typography.Text className={styles.detailValueBold}>
                    <span
                      style={{
                        fontFamily: 'Roboto, sans-serif',
                        fontWeight: 500,
                      }}
                    >
                      ₹
                    </span>
                    {currentValue}
                  </Typography.Text>
                </div>
                <div className={styles.detailItem}>
                  <Typography.Text className={styles.detailLabel}>
                    Quantity
                  </Typography.Text>
                  <Typography.Text className={styles.detailValueBold}>
                    {quantity}
                  </Typography.Text>
                </div>
              </div>

              <div className={styles.divider} />

              <div className={styles.detailRow}>
                <div className={styles.detailItem}>
                  <Typography.Text className={styles.detailLabel}>
                    Last Trade Price
                  </Typography.Text>
                  <Typography.Text className={styles.detailValueBold}>
                    <span
                      style={{
                        fontFamily: 'Roboto, sans-serif',
                        fontWeight: 500,
                      }}
                    >
                      ₹
                    </span>
                    {formatToINRForStocks(holdingData?.bsePrices || '0')}
                  </Typography.Text>
                </div>
                <div className={styles.detailItem}>
                  <Typography.Text className={styles.detailLabel}>
                    Market Capitalisation
                  </Typography.Text>
                  <Typography.Text className={styles.detailValueBold}>
                    {(holdingData?.marketCapValue ?? 0) / 1e7 > 0 && (
                      <span
                        style={{
                          fontFamily: 'Roboto, sans-serif',
                          fontWeight: 500,
                        }}
                      >
                        ₹
                      </span>
                    )}
                    {(holdingData?.marketCapValue ?? 0) / 1e7 > 0
                      ? `${((holdingData?.marketCapValue ?? 0) / 1e7).toFixed(
                          2
                        )} Cr`
                      : '-'}
                  </Typography.Text>
                </div>
              </div>

              <div className={styles.divider} />

              <div className={styles.detailRow}>
                <div className={styles.detailItem}>
                  <Typography.Text className={styles.detailLabel}>
                    Profit/Loss (vs last week){'\n'}Absolute
                  </Typography.Text>
                  <Typography.Text className={styles.detailValueBold}>
                    {holdingData?.lastWeekPL != null && (
                      <span
                        style={{
                          fontFamily: 'Roboto, sans-serif',
                          fontWeight: 500,
                        }}
                      >
                        ₹
                      </span>
                    )}
                    {holdingData?.lastWeekPL != null
                      ? formatToINRForStocks(
                          `${holdingData.lastWeekPL.toFixed(2)}`
                        )
                      : '-'}
                  </Typography.Text>
                </div>
                <div className={styles.detailItem}>
                  <Typography.Text className={styles.detailLabel}>
                    Profit/Loss (vs last week){'\n'}Percentage age
                  </Typography.Text>
                  <Typography.Text className={styles.detailValueBold}>
                    {holdingData?.oneWeekPercentage != null
                      ? `${holdingData.oneWeekPercentage.toFixed(2)}%`
                      : '-'}
                  </Typography.Text>
                </div>
              </div>

              <div className={styles.divider} />

              <div className={styles.detailItem}>
                <Typography.Text className={styles.detailLabel}>
                  P/E Ratio
                </Typography.Text>
                <div className={styles.peValue}>
                  <Typography.Text className={styles.detailValueBold}>
                    {holdingData?.peRatio != null
                      ? `${holdingData.peRatio.toFixed(1)}`
                      : '-'}
                  </Typography.Text>
                  <Typography.Text className={styles.peSubtext}>
                    (vs sector P/E{' '}
                    {holdingData?.sectorPeRatio != null
                      ? `${holdingData.sectorPeRatio.toFixed(1)}`
                      : '-'}{' '}
                    )
                  </Typography.Text>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Card */}
        <div className={styles.performanceCard}>
          <div className={styles.cardHeader}>
            <Typography.Text className={styles.cardTitle}>
              Performance{' '}
            </Typography.Text>
          </div>

          <div className={styles.performanceContent}>
            <div className={styles.performanceRow}>
              <div className={styles.performanceItem}>
                <Typography.Text className={styles.performancePeriod}>
                  1 Month
                </Typography.Text>
                <div className={styles.performanceValue}>
                  <PercentageDisplay value={holdingData?.oneMonthPercentage} />
                </div>
              </div>

              <div className={styles.verticalDivider} />

              <div className={styles.performanceItem}>
                <Typography.Text className={styles.performancePeriod}>
                  3 Months
                </Typography.Text>
                <div className={styles.performanceValue}>
                  <PercentageDisplay
                    value={holdingData?.threeMonthPercentage}
                  />
                </div>
              </div>

              <div className={styles.verticalDivider} />

              <div className={styles.performanceItem}>
                <Typography.Text className={styles.performancePeriod}>
                  1 Year
                </Typography.Text>
                <div className={styles.performanceValue}>
                  <PercentageDisplay value={holdingData?.oneYearPercentage} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {holdingData?.prosConsDTOList?.filter(
          (item: any) => item.category === 'PROS'
        ).length > 0 && (
          <>
            <Typography.Text className={styles.sectionTitle}>
              Pros{' '}
            </Typography.Text>
            <div className={styles.strengthsCard}>
              {holdingData.prosConsDTOList
                .filter((item: any) => item.category === 'PROS')
                .map((item: any, index: number, filteredArray: any[]) => (
                  <React.Fragment key={index}>
                    <div className={styles.listItem}>
                      <ProsIcon />
                      <Typography.Text className={styles.listText}>
                        {item.description}
                      </Typography.Text>
                    </div>
                    {index < filteredArray.length - 1 && (
                      <div className={styles.listDivider} />
                    )}
                  </React.Fragment>
                ))}
            </div>
          </>
        )}

        {holdingData?.prosConsDTOList?.filter(
          (item: any) => item.category === 'CONS'
        ).length > 0 && (
          <>
            <Typography.Text className={styles.sectionTitle}>
              Cons{' '}
            </Typography.Text>
            <div className={styles.risksCard}>
              {holdingData.prosConsDTOList
                .filter((item: any) => item.category === 'CONS')
                .map((item: any, index: number, filteredArray: any[]) => (
                  <React.Fragment key={index}>
                    <div className={styles.listItem}>
                      <CronsIcon />
                      <Typography.Text className={styles.listText}>
                        {item.description}
                      </Typography.Text>
                    </div>
                    {index < filteredArray.length - 1 && (
                      <div className={styles.listDivider} />
                    )}
                  </React.Fragment>
                ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HoldingsScreen;
